/** @jsx jsx */
import {
  ColumnConfig,
  Flex,
  List,
  ListCellsWrapper,
  ListHeader,
  ListHeaderCell,
  ListItem,
  ListItemCell,
  Paragraph,
} from "@eworkit/iris";
import { FC, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { jsx } from "theme-ui";

import { UserContext } from "@/components/User";
import { formatTotalLoggedTime, TimeReportVersion } from "@/data/time-reports";
import { useFormats } from "@/hooks/Formats";
import { router } from "@/views/router";
import { TimeReportStageStatus } from "@/views/time-reports/stages";

interface TimeReportVersionsListProps {
  versions: TimeReportVersion[];
}

export const TimeReportVersionsList: FC<TimeReportVersionsListProps> = ({ versions }) => {
  const { t } = useTranslation("session");

  const { hasCompanyManagerPrivileges } = useContext(UserContext);
  const isManager = hasCompanyManagerPrivileges();

  const { formatCost, formatDate } = useFormats();

  const columns = useMemo(() => {
    const no: ColumnConfig = { sx: { width: [null, null, null, "100px"] } };
    const date: ColumnConfig = { sx: { width: [null, null, null, "250px"] } };
    const amount: ColumnConfig = { sx: { flexGrow: 1 } };
    const status: ColumnConfig = {
      sx: { width: [null, null, null, "200px"], justifyContent: [null, null, null, "flex-end"] },
    };

    return { no, date, amount, status };
  }, []);

  return (
    <Flex flexDirection="column" gap="xs" px={["0", "0", "xs", "0"]}>
      <ListHeader>
        <ListCellsWrapper>
          <ListHeaderCell columnConfig={columns.no}>{t("TIME_REPORTS.LIST.NUMBER_SHORT")}</ListHeaderCell>
          <ListHeaderCell columnConfig={columns.date}>{t("TIME_REPORTS.LIST.CREATING_DATE")}</ListHeaderCell>
          <ListHeaderCell columnConfig={columns.amount}>
            {isManager ? t("TIME_REPORTS.LIST.REPORTED_TIME_COST") : t("TIME_REPORTS.LIST.REPORTED_TIME")}
          </ListHeaderCell>
          <ListHeaderCell columnConfig={columns.status}>{t("TIME_REPORTS.LIST.STATUS")}</ListHeaderCell>
        </ListCellsWrapper>
      </ListHeader>

      <List>
        {versions.map(v => (
          <ListItem key={v.guid}>
            <ListCellsWrapper as={Link} to={router.get("time-reports.details", { timeReportGuid: v.guid }).path}>
              <ListItemCell columnConfig={columns.no}>
                <Paragraph ellipsis>{v.version + 1}</Paragraph>
              </ListItemCell>

              <ListItemCell columnConfig={columns.date}>
                <Paragraph ellipsis>{formatDate(v.creationDate)}</Paragraph>
              </ListItemCell>

              <ListItemCell columnConfig={columns.amount}>
                <Flex flexDirection="column">
                  <Paragraph>{formatTotalLoggedTime(v.totalReportedMinutes, v.totalReportedDays)}</Paragraph>
                  {isManager && v.totalReportedCost && (
                    <Paragraph color="n700">{formatCost(v.totalReportedCost, v.currency.code)}</Paragraph>
                  )}
                </Flex>
              </ListItemCell>

              <ListItemCell columnConfig={columns.status}>
                <TimeReportStageStatus stage={v.stage.code} />
              </ListItemCell>
            </ListCellsWrapper>
          </ListItem>
        ))}
      </List>
    </Flex>
  );
};
